import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { utcToPreferredLocalDateTime } from "../../../utils/timezoneDateConversion";

const resolveRawDateValue = (data, keys = []) => {
    for (const key of keys) {
        const value = data?.[key];
        if (value) return String(value).trim();
    }
    return "";
};

const formatDateTimeLocalWithoutShift = (value) => {
    if (!value) return "";

    const raw = String(value).trim();
    if (!raw) return "";

    const plainMatch = raw.match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
    );

    if (plainMatch) {
        const [, year, month, day, hour, minute] = plainMatch;
        return `${year}-${month}-${day}T${hour}:${minute}`;
    }

    return utcToPreferredLocalDateTime(raw, {
        separator: "T",
        withSeconds: false,
    }) || "";
};

const addOneMinuteToDateTimeLocal = (value) => {
    if (!value) return "";

    const match = value.match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
    );

    if (!match) return value;

    const [, year, month, day, hour, minute] = match;
    const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        0
    );

    if (Number.isNaN(date.getTime())) return value;

    date.setMinutes(date.getMinutes() + 1);

    const nextYear = date.getFullYear();
    const nextMonth = String(date.getMonth() + 1).padStart(2, "0");
    const nextDay = String(date.getDate()).padStart(2, "0");
    const nextHour = String(date.getHours()).padStart(2, "0");
    const nextMinute = String(date.getMinutes()).padStart(2, "0");

    return `${nextYear}-${nextMonth}-${nextDay}T${nextHour}:${nextMinute}`;
};

const buildUploadTimestamp = (value) => {
    if (!value) return "";

    const match = String(value).trim().match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
    );

    if (!match) return "";

    const [, year, month, day, hour, minute] = match;
    return `${year}_${month}_${day}_${hour}_${minute}_00`;
};

const sanitizeFloatInput = (value) => {
    return String(value ?? "").replace(/[^\d.-]/g, "");
};

const isValidFloatValue = (value) => {
    const trimmed = String(value ?? "").trim();

    if (!trimmed) return false;

    if (!/^[-+]?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) {
        return false;
    }

    return Number.isFinite(Number(trimmed));
};

const getDefaultStartDate = (data) =>
    addOneMinuteToDateTimeLocal(
        formatDateTimeLocalWithoutShift(
            resolveRawDateValue(data, [
                "InicioServicio",
                "fechainicio",
                "FechaHoraCita",
                "fecha",
            ])
        )
    );

const getDefaultEndDate = (data) =>
    formatDateTimeLocalWithoutShift(
        resolveRawDateValue(data, [
            "FinServicio",
            "fechafin",
            "FechaHoraDescargue",
        ])
    );

const ModalVideo = (props) => {
    const [contrato, setContrato] = useState(props.selectedContrato || "");
    const [latitud, setLatitud] = useState("");
    const [longitud, setLongitud] = useState("");
    const [fechaVideo, setFechaVideo] = useState(() => getDefaultStartDate(props.data));
    const [evento, setEvento] = useState(props.eventoSeleccionado || props.listEventos[0]?.ID_Evento);
    const [videos, setVideos] = useState([]);
    const username = localStorage.getItem("username") || "unknown_user";

    const fileInputRef = useRef(null);

    useEffect(() => {
        setContrato(props.selectedContrato || "");
        setEvento(props.eventoSeleccionado || props.listEventos[0]?.ID_Evento);
    }, [props.selectedContrato, props.eventoSeleccionado, props.listEventos]);

    useEffect(() => {
        setFechaVideo(getDefaultStartDate(props.data));
    }, [props.data]);

    const handleFileChange = (event) => {
        setVideos(event.target.files);
    };

    const uploadVideo = async () => {
        const latitudValue = sanitizeFloatInput(latitud);
        const longitudValue = sanitizeFloatInput(longitud);

        if (!isValidFloatValue(latitudValue) || !isValidFloatValue(longitudValue)) {
            Swal.fire("Error", "Latitud o Longitud invalida", "error");
            return;
        }

        const fechaLocal = buildUploadTimestamp(fechaVideo);
        if (!fechaLocal) {
            Swal.fire("Error", "Fecha invalida", "error");
            return;
        }

        if (videos.length === 0) {
            Swal.fire("Error", "No se han seleccionado archivos", "error");
            return;
        }

        const formData = new FormData();

        for (let i = 0; i < videos.length; i += 1) {
            const file = videos[i];
            const newName = `${fechaLocal}_${i}_${props.device || "nodata"}_${props.placa || "nodata"}_${evento || "nodata"}_${Math.floor(Math.random() * 1000)}_${username}_${latitudValue}_${longitudValue}`;

            if (file.type.startsWith("image/")) {
                formData.append("files", file, `${newName}.jpg`);
            } else if (file.type.startsWith("video/")) {
                formData.append("files", file, `${newName}.mp4`);
            }
        }

        Swal.fire({
            title: "Subiendo Video...",
            timerProgressBar: true,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const response = await fetch(process.env.REACT_APP_SERVER + "/uploadvideo", {
                method: "POST",
                body: formData,
            });

            const data = await response.json();

            if (data?.success) {
                Swal.close();
                Swal.fire({
                    icon: "success",
                    title: "Exito",
                    text: "Video subido correctamente",
                    timer: 1000,
                    showConfirmButton: false,
                });
                setVideos([]);
                if (fileInputRef.current) fileInputRef.current.value = "";
                props.onClose();
            } else {
                Swal.fire("Error", "Hubo un problema al subir el video", "error");
            }
        } catch (error) {
            Swal.close();
            console.error(error);
            Swal.fire("Error", "Error al subir video", "error");
        }
    };

    const minFecha = getDefaultStartDate(props.data) || undefined;
    const maxFecha = getDefaultEndDate(props.data) || undefined;

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="mb-3">
                        <label className="form-label" htmlFor="contrato">Contrato</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text">
                                <i className="bx bx-file-blank"></i>
                            </span>
                            <input type="text" className="form-control" value={contrato || props.selectedContrato} readOnly />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="latitud">Latitud</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-map-pin"></i></span>
                            <input
                                type="text"
                                className="form-control"
                                value={latitud}
                                inputMode="decimal"
                                placeholder="0.00"
                                onChange={(e) => setLatitud(sanitizeFloatInput(e.target.value))}
                            />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="longitud">Longitud</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-map-pin"></i></span>
                            <input
                                type="text"
                                className="form-control"
                                value={longitud}
                                inputMode="decimal"
                                placeholder="0.00"
                                onChange={(e) => setLongitud(sanitizeFloatInput(e.target.value))}
                            />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="fechaVideo">Hora/Fecha</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-calendar"></i></span>
                            <input type="datetime-local" className="form-control" value={fechaVideo} onChange={(e) => setFechaVideo(e.target.value)} min={minFecha} max={maxFecha} />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="eventos">Evento</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-file-blank"></i></span>
                            <select className="form-select" value={evento} onChange={(e) => setEvento(e.target.value)}>
                                {props.listEventos?.map((loc) => (
                                    <option key={loc.ID_Evento} value={loc.ID_Evento}>{loc.Descripcion}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="videos">Videos</label>
                        <div className="input-group input-group-merge">
                            <span className="input-group-text"><i className="bx bx-file"></i></span>
                            <input type="file" className="form-control" multiple ref={fileInputRef} onChange={handleFileChange} />
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary" onClick={props.onClose}>
                        Cerrar
                    </button>
                    <button type="button" className="btn btn-primary" onClick={uploadVideo}>
                        Guardar
                    </button>
                </div>
            </div>
        </>
    );
};

export default ModalVideo;
